import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  type OnFarmImplantApiSourceType,
  OnFarmImplantRequestError,
  type OnFarmImplantSessionEmbryo,
  useAddOnFarmImplantQueue,
} from '@/services/on-farm-implant';

type AddToQueueDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  embryos: OnFarmImplantSessionEmbryo[];
  onQueued: () => void;
  sourceType?: OnFarmImplantApiSourceType;
};

function defaultStrawValue(embryo: OnFarmImplantSessionEmbryo): string {
  const straw = embryo.transferStrawNumber ?? embryo.frozenStrawNumber;
  return straw === null || straw === undefined ? '' : String(straw);
}

function parseStraw(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return parsed > 0 ? parsed : null;
}

export const AddToQueueDialog = ({
  open,
  onOpenChange,
  sessionId,
  embryos,
  onQueued,
  sourceType = 'frozen',
}: AddToQueueDialogProps) => {
  const { t } = useTranslation();

  const [strawEdits, setStrawEdits] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: addToQueue, isPending } = useAddOnFarmImplantQueue();
  const isNonTog = sourceType === 'non-tog';

  const strawValueFor = useCallback(
    (embryo: OnFarmImplantSessionEmbryo) =>
      strawEdits[embryo.sessionEmbryoId] ?? defaultStrawValue(embryo),
    [strawEdits],
  );

  const invalidIds = useMemo(
    () =>
      new Set(
        isNonTog
          ? []
          : embryos
              .filter((embryo) => parseStraw(strawValueFor(embryo)) === null)
              .map((embryo) => embryo.sessionEmbryoId),
      ),
    [embryos, isNonTog, strawValueFor],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setStrawEdits({});
        setProblems([]);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleStrawChange = useCallback(
    (sessionEmbryoId: string, value: string) => {
      setStrawEdits((current) => ({ ...current, [sessionEmbryoId]: value }));
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (embryos.length === 0 || invalidIds.size > 0) return;

    setProblems([]);

    try {
      const result = await addToQueue({
        sessionId,
        sourceType,
        input: {
          items: embryos.map((embryo) => ({
            sessionEmbryoId: embryo.sessionEmbryoId,
            transferStrawNumber: isNonTog
              ? null
              : parseStraw(strawValueFor(embryo)),
          })),
          versions: collectVersions(embryos),
        },
      });

      setStrawEdits({});
      onOpenChange(false);
      onQueued();

      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.frozen.queue.success', {
          count: result.totalQueued,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to add embryos to the implant queue:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.frozen.queue.failed'),
      });
    }
  }, [
    addToQueue,
    embryos,
    invalidIds,
    isNonTog,
    onOpenChange,
    onQueued,
    sessionId,
    sourceType,
    strawValueFor,
    t,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('onFarmImplant.frozen.queue.title')}</DialogTitle>
          <DialogDescription>
            {t(
              sourceType === 'client-on-farm'
                ? 'onFarmImplant.clientOnFarm.queue.description'
                : isNonTog
                  ? 'onFarmImplant.nonTog.queue.description'
                  : 'onFarmImplant.frozen.queue.description',
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.embryo')}
                </th>
                {!isNonTog && (
                  <>
                    <th className="py-1 font-medium">
                      {t('onFarmImplant.frozen.frozenStraw')}
                    </th>
                    <th className="py-1 font-medium">
                      {t('onFarmImplant.frozen.transferStraw')}
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {embryos.map((embryo) => {
                const inputId = `transfer-straw-${embryo.sessionEmbryoId}`;
                const isInvalid = invalidIds.has(embryo.sessionEmbryoId);

                return (
                  <tr key={embryo.sessionEmbryoId} className="border-t">
                    <td className="py-1.5">{embryo.embryoName ?? '—'}</td>
                    {!isNonTog && (
                      <>
                        <td className="py-1.5">
                          {embryo.frozenStrawNumber ?? '—'}
                        </td>
                        <td className="py-1.5">
                          <label className="sr-only" htmlFor={inputId}>
                            {t('onFarmImplant.frozen.queue.transferStrawFor', {
                              embryo:
                                embryo.embryoName ?? embryo.sessionEmbryoId,
                            })}
                          </label>
                          <Input
                            id={inputId}
                            type="number"
                            min={1}
                            className="h-7 w-24 text-xs"
                            aria-invalid={isInvalid}
                            value={strawValueFor(embryo)}
                            onChange={(event) =>
                              handleStrawChange(
                                embryo.sessionEmbryoId,
                                event.target.value,
                              )
                            }
                          />
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {invalidIds.size > 0 && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.frozen.queue.invalidStraw')}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={embryos.length === 0 || invalidIds.size > 0 || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.frozen.queue.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
